// API Configuration
// Helper function to get API base URL with /api appended
// VITE_API_BASE_URL should not include /api (e.g., http://31.220.82.50:8098)
const getApiBaseUrl = () => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
  // Remove trailing slashes
  let normalized = baseUrl.replace(/\/+$/, '');
  // If baseUrl is just '/api', return it as is (for local development)
  if (normalized === '/api') {
    return normalized;
  }
  // Otherwise, ensure /api is appended
  if (!normalized.endsWith('/api')) {
    normalized = normalized + '/api';
  }
  return normalized;
};

const API_BASE_URL = getApiBaseUrl();

// Export helper function for use in other files
export const getNormalizedApiBaseUrl = getApiBaseUrl;

// Global error handler for 401 responses
let globalUnauthorizedHandler = null;
let refreshPromise = null;

// Track recently logged errors to prevent duplicates (from retries)
const loggedErrors = new Map();
const ERROR_LOG_DEDUP_WINDOW = 5000; // 5 seconds - prevent duplicate logs within this window

// Clean up old entries periodically
setInterval(() => {
  const now = Date.now();
  for (const [key, timestamp] of loggedErrors.entries()) {
    if (now - timestamp > ERROR_LOG_DEDUP_WINDOW) {
      loggedErrors.delete(key);
    }
  }
}, ERROR_LOG_DEDUP_WINDOW);

export const setGlobalUnauthorizedHandler = (handler) => {
  globalUnauthorizedHandler = handler;
};

export const handleUnauthorized = () => {
  if (globalUnauthorizedHandler) {
    globalUnauthorizedHandler();
  }
};

// Common headers
export const getHeaders = (token = null, language = null) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (language) {
    headers['Accept-Language'] = language;
  }

  return headers;
};

// Get user's preferred language from localStorage (or sessionStorage for backward compatibility)
export const getUserPreferredLanguage = () => {
  try {
    // Check localStorage first, then sessionStorage for backward compatibility
    const userData = localStorage.getItem('inchaa_user') || sessionStorage.getItem('inchaa_user');
    if (userData) {
      const user = JSON.parse(userData);
      return user.preferredLanguage || 'en';
    }
  } catch (error) {
    console.error('Error getting user preferred language:', error);
  }
  return 'en'; // Default to English
};

// Public API endpoints (no authentication required)
export const PUBLIC_ENDPOINTS = {
  // Auth (public)
  LOGIN: '/Auth/login',
  REFRESH_TOKEN: '/Auth/refresh-token',
  REGISTER_PROVIDER: '/Auth/register-provider',
  ALL_ROLES: '/Auth/all-roles',
  ROLES_BY_USER_TYPE: (userType) => `/Auth/rolesbyusertype?userType=${userType}`,
  SEND_MOBILE_OTP: (phoneNumber) => `/Auth/send-mobile-otp?phoneNumber=${encodeURIComponent(phoneNumber)}`,
  SEND_EMAIL_OTP: (email) => `/Auth/send-email-otp?email=${email}`,
  REQUEST_FORGOT_PASSWORD: (email) => `/Auth/request-forget-password?email=${encodeURIComponent(email)}`,
  FORGOT_PASSWORD: '/Auth/forgot-password',
  RESET_PASSWORD: '/Auth/reset-password',
  VERIFY_EMAIL_OTP: '/Auth/verify-email-otp',
  VERIFY_MOBILE_OTP: '/Auth/verify-mobile-otp',
  SECURE_VERIFY_EMAIL_OTP: '/Auth/secure-verify-email-otp',
  SECURE_VERIFY_MOBILE_OTP: '/Auth/secure-verify-mobile-otp',
  CHECK_EMAIL_PHONE_AVAILABILITY: '/Auth/check-email-phone-availability',
  UPDATE_SERVICE_PROVIDER_LICENSE: '/Auth/update-service-provider-license',
  REJECTED_USER_PROFILE: '/Auth/rejected-user-profile',
  RE_REGISTER_REJECTED_PROVIDER: '/Auth/re-register-rejected-provider',
  CHANGE_PHONE_NUMBER: '/Auth/change-phone-number',

  // Public data
  SERVICES_OFFERED: '/AppHome/subcontractor-services',
  MAIN_CONTRACTOR_SERVICES: '/AppHome/main-contractor-services',
  LOCATIONS: '/AppHome/locations',
  ACTIVE_OFFERS: '/AppHome/offers',
  MATERIALS_HIERARCHY: '/AppHome/materials/hierarchy',
  ENGINEER_SERVICES: '/AppHome/v2/engineer-services',

  // Public file upload (for license updates)
  PRESIGNED_URLS: '/Assets/presign-urls',
};

// Authenticated API endpoints (require access token)
export const AUTH_ENDPOINTS = {
  // Posts / Ads
  PUBLIC_ADS: (pageNumber = 1, pageSize = 10, filters = {}) => {
    const params = new URLSearchParams();
    params.set('pageNumber', String(pageNumber));
    params.set('pageSize', String(pageSize));

    // roleTypeId can be a single value or repeated values (roleTypeId=1&roleTypeId=2)
    if (Array.isArray(filters?.roleTypeIds) && filters.roleTypeIds.length > 0) {
      filters.roleTypeIds.forEach((id) => {
        if (id !== null && id !== undefined && id !== '') params.append('roleTypeId', String(id));
      });
    } else if (filters?.roleTypeId) {
      params.set('roleTypeId', String(filters.roleTypeId));
    }
    if (filters?.locationId) params.set('locationId', String(filters.locationId));
    if (filters?.sortBy) params.set('sortBy', String(filters.sortBy));
    if (filters?.isEnrolled === true || filters?.isEnrolled === false) {
      params.set('isEnrolled', String(filters.isEnrolled));
    }
    if (Array.isArray(filters?.serviceIds) && filters.serviceIds.length > 0) {
      filters.serviceIds.forEach((id) => {
        if (id !== null && id !== undefined && id !== '') params.append('serviceIds', String(id));
      });
    }

    return `/Posts/get-public-ads?${params.toString()}`;
  },

  INTERESTED_ACTIVE_ADS: (pageNumber = 1, pageSize = 10, filters = {}) => {
    const params = new URLSearchParams();
    params.set('pageNumber', String(pageNumber));
    params.set('pageSize', String(pageSize));
    if (filters.sortBy) params.set('sortBy', String(filters.sortBy));
    if (filters.roleType_filter != null && filters.roleType_filter !== '') {
      params.set('roleType_filter', String(filters.roleType_filter));
    }
    if (filters.serviceType_filter != null && filters.serviceType_filter !== '') {
      params.set('serviceType_filter', String(filters.serviceType_filter));
    }
    if (filters.location_filter != null && filters.location_filter !== '') {
      params.set('location_filter', String(filters.location_filter));
    }
    if (filters.filterQuotationStatus != null && filters.filterQuotationStatus !== '') {
      const statuses = Array.isArray(filters.filterQuotationStatus)
        ? filters.filterQuotationStatus.filter((s) => s != null && s !== '')
        : [String(filters.filterQuotationStatus)];
      statuses.forEach((s) => params.append('filterQuotationStatus', String(s)));
    }
    return `/Posts/interested-active-ads?${params.toString()}`;
  },

  // Admin endpoints
  CONTRACTORS: (page = 1, pageSize = 10) => {
    return `/Admin/contractors?page=${page}&pageSize=${pageSize}`;
  },
  SERVICE_PROVIDERS: (page = 1, pageSize = 10) => {
    return `/Admin/service-providers?page=${page}&pageSize=${pageSize}`;
  },
  CUSTOMERS: (page = 1, pageSize = 10) => {
    return `/Admin/customers?page=${page}&pageSize=${pageSize}`;
  },
  CUSTOMER_DETAILS: (id) => `/Admin/customers/${id}`,
  ALL_USERS: (page = 1, pageSize = 10) => `/Admin/users?page=${page}&pageSize=${pageSize}`,
  // Service Provider details
  CONTRACTOR_DETAILS: (id) => `/ServiceProvider/${id}`,
  CONTRACTOR_APPROVAL: `/Admin/change-approval-status`,
  CONTRACTOR_LICENSE_APPROVAL: `/Admin/change-license-status`,
  REQUEST_RESUBMISSION: `/Admin/request-resubmission`,
  SERVICE_PROVIDERS_BY_LICENSE: (page = 1, pageSize = 10, statuses = []) => {
    let endpoint = `/Admin/service-providers-by-license?page=${page}&pageSize=${pageSize}`;
    // Add status parameters if provided
    if (Array.isArray(statuses) && statuses.length > 0) {
      statuses.forEach(status => {
        endpoint += `&Status=${encodeURIComponent(status)}`;
      });
    }
    return endpoint;
  },
  // Dashboard endpoints
  DASHBOARD_STATS: '/Users/dashboardStats',
  // Quotation endpoints
  GET_QUOTATIONS: '/Quotation/get-quotations',
  SUBMIT_QUOTATION: '/Quotation/submit',
  QUOTATION_OVERVIEW: (id) => `/Quotation/overview/${id}`,
  QUOTATION_DETAIL: (id) => `/Quotation/detail/${id}`,
  QUOTATION_TIMELINE: (quotationId) => `/Quotation/timeline?quotationId=${quotationId}`,
  CHANGE_QUOTATION_STATUS: '/Quotation/change-status',
  /** POST body: { quotationId, rescheduleDate, timeSlots: number[], requestReason } — hourFrom values 9,11,13,15,17. */
  REQUEST_QUOTATION_RESCHEDULE: '/Quotation/request-reschedule',
  SERVICE_PROVIDER_QUOTATIONS: (serviceProviderId) => `/Admin/service-provider-quotations?serviceProviderId=${serviceProviderId}`,
  SERVICE_PROVIDER_QUOTATION_OVERVIEW: (quotationId) => `/Admin/service-provider-quotation-overview?quotationId=${quotationId}`,
  CUSTOMER_QUOTATIONS: (customerId) => `/Admin/customer-quotations?customerId=${customerId}`,
  ALL_QUOTATIONS: (page = 1, pageSize = 10, filters = {}) => {
    let endpoint = `/Admin/all-quotations?page=${page}&pageSize=${pageSize}`;

    // Add filters if provided
    if (filters.fromDate) {
      endpoint += `&FromDate=${encodeURIComponent(filters.fromDate)}`;
    }
    if (filters.toDate) {
      endpoint += `&ToDate=${encodeURIComponent(filters.toDate)}`;
    }
    if (filters.search) {
      endpoint += `&Search=${encodeURIComponent(filters.search)}`;
    }
    // Handle both single status string and array of statuses
    if (filters.status) {
      if (Array.isArray(filters.status)) {
        // If status is an array, append each status as a separate Status parameter
        filters.status.forEach(s => {
          endpoint += `&Status=${encodeURIComponent(s)}`;
        });
      } else {
        // If status is a single string, append it once
        endpoint += `&Status=${encodeURIComponent(filters.status)}`;
      }
    }
    if (filters.type && filters.type !== 'all') {
      // Map human-readable filter values to API values (lowercase, no spaces for RequestedRoleFilter)
      const apiTypeMap = {
        'Main Contractor': 'maincontractor',
        'Sub Contractor': 'subcontractor',
        'Engineer': 'engineer'
      };
      const apiType = apiTypeMap[filters.type] || filters.type.toLowerCase().replace(/\s+/g, '');
      endpoint += `&RequestedRoleFilter=${encodeURIComponent(apiType)}`;
    }
    if (filters.sortTypes && filters.sortTypes.length > 0) {
      filters.sortTypes.forEach(sortType => {
        endpoint += `&SortTypes=${encodeURIComponent(sortType)}`;
      });
    } else if (filters.sortType) {
      endpoint += `&SortTypes=${encodeURIComponent(filters.sortType)}`;
    }

    return endpoint;
  },
  // User endpoints
  UPDATE_AVAILABILITY: (isAvailable) => `/Users/availability?isAvailable=${isAvailable}`,
  /** GET — booked / already-chosen slots: { date, timeSlots: [{ hourFrom, hourTo }] } (may be wrapped in result). Pass current booking `scheduledDate` as YYYY-MM-DD. */
  USER_AVAILABILITY_TIMESLOTS: '/Users/availability/timeslots',
  USER_AVAILABILITY_TIMESLOTS_FOR_DATE: (date) =>
    `/Users/availability/timeslots?date=${encodeURIComponent(String(date))}`,
  // Services endpoints
  GET_SERVICES: '/AppHome/services',
  // Locations endpoints
  GET_LOCATIONS: '/AppHome/locations',
  // Questionnaire endpoints
  QUESTIONNAIRE_FORM_QUESTIONS: '/Questionnaire/get-form-questions',
  // Posts endpoints
  POSTS_CREATE: '/admin/posts',
  ADMIN_POSTS_GET: (pageNumber = 1, pageSize = 10, roleTypeId = "", locationId = "", sortBy = "", filterStatus = "") =>
    `/Admin/posts/get?pageNumber=${pageNumber}&pageSize=${pageSize}&roleTypeId=${roleTypeId}&locationId=${locationId}${sortBy ? `&sortBy=${encodeURIComponent(sortBy)}` : ''}${filterStatus ? `&filterStatus=${encodeURIComponent(filterStatus)}` : ''}`,
  ADMIN_POST_OVERVIEW: (postId) => `/Admin/posts/overview/${postId}`,
  ADMIN_POST_UPDATE: (postId) => `/Admin/posts/${postId}`,
  POST_OVERVIEW: (postId) => `/Posts/overview/${postId}`,
  POST_ENROLL_SERVICE_PROVIDER: '/Posts/enroll-service-provider',
  // Property endpoints
  PROPERTY_ADMIN_USER: (userId) => `/Admin/properties/${userId}`,
  PROPERTIES_CREATE: '/Admin/properties/create',
  PROPERTIES_UPDATE: '/Admin/properties/update',
  PROPERTY_SET_DEFAULT: '/Admin/properties/set-default',
  // File upload endpoints
  PRESIGNED_URLS: '/Assets/presign-urls',
  // User profile endpoints
  UPDATE_USER_PROFILE_ASSETS: '/Users/updateuserprofileassets',
  UPDATE_PROFILE: '/Users/update-profile',
  USER_PROFILE: '/Users/profile',
  REJECTED_USER_PROFILE: '/Auth/rejected-user-profile',
  RE_REGISTER_REJECTED_PROVIDER: '/Auth/re-register-rejected-provider',
  // Language endpoints
  UPDATE_LANGUAGE: '/Auth/update-language',
  // Phone number endpoints
  CHANGE_PHONE_NUMBER: '/Auth/change-phone-number',
  // Notification endpoints
  GET_ALL_NOTIFICATIONS: '/Notification/GetAllNotifications',
  SET_READ_ALL: '/Notification/SetReadAll',
  GET_UNREAD_COUNT: '/Notification/GetUnreadCount',

  // Top Service Providers endpoints
  TOP_SERVICE_PROVIDERS: (userTypeId) => `/Admin/top-service-providers?userTypeId=${userTypeId}`,
  SAVE_TOP_SERVICE_PROVIDERS: '/Admin/top-service-providers',
  // Offers endpoints
  GET_OFFERS: '/Offers/Get', // New endpoint for getting offers with search and filter
  GET_OFFER: (id) => `/Admin/offers/${id}`, // GET single offer by ID
  CREATE_OFFER: '/Offers', // New endpoint for creating offers
  UPDATE_OFFER: '/Offers', // PUT method with offerId in body
  DELETE_OFFER: (id) => `/Offers/${id}`, // DELETE method
  REORDER_OFFERS: '/Admin/offers/reorder',
  UPDATE_OFFER_STATUS: (id) => `/Admin/offers/${id}/status`,
  SAVE_OFFER_ORDER: '/Offers/order', // New endpoint for saving offer order

  /** GET /Wallet/get — engineer wallet payments (paginated). Query: Search, RoleName, Status, Page, PageSize */
  WALLET_GET: (filters = {}) => {
    const params = new URLSearchParams();
    const search = filters.search != null ? String(filters.search).trim() : '';
    if (search) params.set('Search', search);
    const roleName = filters.roleName != null ? String(filters.roleName).trim() : '';
    if (roleName) params.set('RoleName', roleName);
    const status = filters.status != null ? String(filters.status).trim() : '';
    if (status) params.set('Status', status);
    const page = Number(filters.page);
    const pageSize = Number(filters.pageSize);
    params.set('Page', String(Number.isFinite(page) && page > 0 ? page : 1));
    params.set('PageSize', String(Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 10));
    return `/Wallet/get?${params.toString()}`;
  },

  /** GET /Wallet/payment/{id} — single payment detail (breakdown, timeline). */
  WALLET_PAYMENT_GET: (paymentId) => `/Wallet/payment/${paymentId}`,

  // Material Master endpoints
  GET_MATERIAL_HIERARCHY: '/AppHome/materials/hierarchy',
  GET_MAIN_CATEGORIES: '/Admin/materials/main-categories',
  GET_MAIN_CATEGORY_BY_ID: (id) => `/Admin/materials/main-categories/${id}`,
  GET_SUB_CATEGORIES: '/Admin/materials/sub-categories',
  GET_SUB_CATEGORY_BY_ID: (id) => `/Admin/materials/sub-categories/${id}`,
  GET_MATERIALS: '/Admin/materials',
  GET_MATERIAL_BY_ID: (id) => `/Admin/materials/${id}`,
  CREATE_MAIN_CATEGORY: '/Admin/materials/main-categories',
  UPDATE_MAIN_CATEGORY: (id) => `/Admin/materials/main-categories/${id}`,
  DELETE_MAIN_CATEGORY: (id) => `/Admin/materials/main-categories/${id}`,
  REMOVE_MAIN_CATEGORY_IMAGE: (id) => `/Admin/materials/main-categories/${id}/image`,
  CREATE_SUB_CATEGORY: '/Admin/materials/sub-categories',
  UPDATE_SUB_CATEGORY: (id) => `/Admin/materials/sub-categories/${id}`,
  DELETE_SUB_CATEGORY: (id) => `/Admin/materials/sub-categories/${id}`,
  REMOVE_SUB_CATEGORY_IMAGE: (id) => `/Admin/materials/sub-categories/${id}/image`,
  CREATE_MATERIAL: '/Admin/materials',
  UPDATE_MATERIAL: (id) => `/Admin/materials/${id}`,
  DELETE_MATERIAL: (id) => `/Admin/materials/${id}`,
  REMOVE_MATERIAL_IMAGE: (id) => `/Admin/materials/${id}/image`,
  ARCHIVE_MAIN_CATEGORY: (id) => `/Admin/materials/main-categories/${id}/archive`,
  ARCHIVE_SUB_CATEGORY: (id) => `/Admin/materials/sub-categories/${id}/archive`,
  ARCHIVE_MATERIAL: (id) => `/Admin/materials/${id}/archive`,
  CREATE_MATERIAL_SIZE: (materialId) => `/Admin/materials/${materialId}/sizes`,
  UPDATE_MATERIAL_SIZE: (sizeId) => `/Admin/materials/${sizeId}/sizes`,
  DELETE_MATERIAL_SIZE: (sizeId) => `/Admin/materials/material-sizes/${sizeId}`,
  ADD_MATERIAL_BRAND: (materialId) => `/Admin/materials/${materialId}/brands`,
  UPDATE_MATERIAL_BRAND: (materialBrandId) => `/Admin/materials/${materialBrandId}/brands`,
  DELETE_MATERIAL_BRAND: (materialBrandId) => `/Admin/materials/material-brands/${materialBrandId}`,
};

// Legacy support - keeping the old structure for backward compatibility
export const API_ENDPOINTS = {
  ...PUBLIC_ENDPOINTS,
};

// API utility helpers
const parseJsonResponse = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return { message: `HTTP error! status: ${response.status}` };
  }
};

/** True when the failure is likely offline / transient — do not clear auth tokens in this case. */
function isLikelyNetworkError(error) {
  if (!error) return false;
  if (error.name === 'TypeError') return true;
  const msg = String(error.message || '');
  if (/failed to fetch|networkerror|load failed|aborted|timeout|network request failed/i.test(msg)) {
    return true;
  }
  return false;
}

const saveTokens = ({ accessToken, refreshToken }) => {
  if (accessToken) {
    localStorage.setItem('access_token', accessToken);
  }
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
  }
};

export const refreshAccessToken = async () => {
  const storedRefreshToken = localStorage.getItem('refresh_token');
  const storedAccessToken = localStorage.getItem('access_token');
  if (!storedRefreshToken) {
    return null;
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  let userId = null;
  try {
    const raw = sessionStorage.getItem('inchaa_user');
    if (raw) {
      const parsed = JSON.parse(raw);
      userId = parsed?.id || parsed?.userId || null;
    }
  } catch (error) {
    console.warn('Failed to parse stored user for refresh token payload', error);
  }

  const payload = {
    refreshToken: storedRefreshToken,
  };

  if (storedAccessToken) {
    payload.accessToken = storedAccessToken;
  }

  if (userId) {
    payload.userId = userId;
  }

  const refreshUrl = `${API_BASE_URL}${PUBLIC_ENDPOINTS.REFRESH_TOKEN}?refreshToken=${encodeURIComponent(storedRefreshToken)}`;

  refreshPromise = (async () => {
    try {
      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await parseJsonResponse(response);

      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to refresh token');
      }

      const result = data?.result || data;
      const newAccessToken = result?.accessToken;
      const newRefreshToken = result?.refreshToken || storedRefreshToken;

      if (!newAccessToken) {
        throw new Error('Refresh response missing access token');
      }

      saveTokens({ accessToken: newAccessToken, refreshToken: newRefreshToken });
      return { accessToken: newAccessToken, refreshToken: newRefreshToken };
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Only clear stored tokens on real auth failures — not on offline / fetch failures (avoids bogus 401 after reconnect).
      if (!isLikelyNetworkError(error)) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const config = {
    ...options,
    headers: options.headers ? { ...options.headers } : getHeaders(),
  };

  const hasAuthHeader = Boolean(config.headers?.Authorization);

  const executeRequest = async (isRetry = false) => {
    const response = await fetch(url, config);
    const responseData = await parseJsonResponse(response);

    if (response.status === 401 && hasAuthHeader && !isRetry) {
      const refreshedTokens = await refreshAccessToken();
      if (refreshedTokens?.accessToken) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${refreshedTokens.accessToken}`,
        };
        return executeRequest(true);
      }

      // Refresh failed but user still has a refresh token (e.g. offline during refresh) — do not force logout.
      if (localStorage.getItem('refresh_token')) {
        const err = new Error(
          responseData?.message ||
            'Unable to reach the server to refresh your session. Check your connection and try again.'
        );
        err.response = { data: responseData, status: 401, code: 'SESSION_REFRESH_FAILED' };
        throw err;
      }

      handleUnauthorized();
    }

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`);
      error.response = { data: responseData, status: response.status };
      throw error;
    }

    return responseData;
  };

  try {
    return await executeRequest();
  } catch (error) {
    console.error('API request failed:', error);

    // Log error to S3 silently (only if not an error from the logging endpoint itself to avoid infinite loops)
    // This runs asynchronously and will not affect the main error flow or user experience
    if (!endpoint.includes('/Assets/logging-presign-urls')) {
      // Create a unique key for this error to prevent duplicate logging from retries
      const errorStatus = error?.response?.status || 'Unknown';
      const errorMessage = error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'API request failed';
      const errorKey = `${endpoint}:${errorStatus}:${errorMessage}`;
      const now = Date.now();
      const lastLogged = loggedErrors.get(errorKey);

      // Only log if we haven't logged this exact error recently (within dedup window)
      if (!lastLogged || (now - lastLogged) > ERROR_LOG_DEDUP_WINDOW) {
        // Mark this error as logged
        loggedErrors.set(errorKey, now);

        // Run error logging asynchronously without blocking or affecting the main flow
        (async () => {
          try {
            // Get user ID from storage
            let userId = null;
            try {
              const userData = localStorage.getItem('inchaa_user') || sessionStorage.getItem('inchaa_user');
              if (userData) {
                const user = JSON.parse(userData);
                userId = user?.id || user?.userId || user?.memberId || null;
              }
            } catch (parseError) {
              // Silently fail - don't log anything
            }

            // Import and call storeErrorLog dynamically to avoid circular dependencies
            const module = await import('../utils/storeErrorLogs');
            const storeErrorLog = module.default;
            // Create error info object matching the expected format
            const errorInfo = {
              message: errorMessage,
              stack: error?.stack || `${endpoint} - HTTP ${errorStatus}`
            };
            // Get API response data if available
            const apiResponseData = error?.response?.data || null;
            // Get full API URL for logging
            const apiBaseUrl = getNormalizedApiBaseUrl();
            const fullApiUrl = `${apiBaseUrl}${endpoint}`;
            // Call storeErrorLog and silently catch any errors
            await storeErrorLog(errorInfo, userId, fullApiUrl, apiResponseData).catch(() => {
              // Silently fail - no logging, no user indication, no impact on other calls
            });
          } catch (logError) {
            // Silently fail - no logging, no user indication, no impact on other calls
          }
        })();
      }
    }

    throw error;
  }
};

// Public API service (no authentication required)
export const publicApiService = {
  // GET request
  get: (endpoint, language = null) => {
    return apiRequest(endpoint, {
      method: 'GET',
      headers: getHeaders(null, language), // No token, but may have language
    });
  },

  // POST request
  post: (endpoint, data, language = null) => {
    return apiRequest(endpoint, {
      method: 'POST',
      headers: getHeaders(null, language), // No token, but may have language
      body: JSON.stringify(data),
    });
  },
};

// Authenticated API service (requires access token)
export const authApiService = {
  // GET request
  get: (endpoint, languageOverride = null) => {
    const token = localStorage.getItem('access_token');
    const language = languageOverride || getUserPreferredLanguage();
    return apiRequest(endpoint, {
      method: 'GET',
      headers: getHeaders(token, language),
    });
  },

  // POST request
  post: (endpoint, data, languageOverride = null) => {
    const token = localStorage.getItem('access_token');
    const language = languageOverride || getUserPreferredLanguage();
    return apiRequest(endpoint, {
      method: 'POST',
      headers: getHeaders(token, language),
      body: JSON.stringify(data),
    });
  },

  // PUT request
  put: (endpoint, data, languageOverride = null) => {
    const token = localStorage.getItem('access_token');
    const language = languageOverride || getUserPreferredLanguage();
    return apiRequest(endpoint, {
      method: 'PUT',
      headers: getHeaders(token, language),
      body: JSON.stringify(data),
    });
  },

  // PATCH request
  patch: (endpoint, data, languageOverride = null) => {
    const token = localStorage.getItem('access_token');
    const language = languageOverride || getUserPreferredLanguage();
    return apiRequest(endpoint, {
      method: 'PATCH',
      headers: getHeaders(token, language),
      body: JSON.stringify(data),
    });
  },

  // DELETE request
  delete: (endpoint, languageOverride = null) => {
    const token = localStorage.getItem('access_token');
    const language = languageOverride || getUserPreferredLanguage();
    return apiRequest(endpoint, {
      method: 'DELETE',
      headers: getHeaders(token, language),
    });
  },

};



// Legacy API service (for backward compatibility)
// API service (for authenticated requests)
export const apiService = {
  // GET request
  get: (endpoint, token = null) => {
    const language = token ? getUserPreferredLanguage() : null; // Only add language header if token is provided (authenticated request)
    return apiRequest(endpoint, {
      method: 'GET',
      headers: getHeaders(token, language),
    });
  },

  // POST request
  post: (endpoint, data, token = null) => {
    const language = token ? getUserPreferredLanguage() : null; // Only add language header if token is provided (authenticated request)
    return apiRequest(endpoint, {
      method: 'POST',
      headers: getHeaders(token, language),
      body: JSON.stringify(data),
    });
  },

  // PUT request
  put: (endpoint, data, token = null) => {
    const language = token ? getUserPreferredLanguage() : null; // Only add language header if token is provided (authenticated request)
    return apiRequest(endpoint, {
      method: 'PUT',
      headers: getHeaders(token, language),
      body: JSON.stringify(data),
    });
  },

  // PATCH request
  patch: (endpoint, data, token = null) => {
    const language = token ? getUserPreferredLanguage() : null; // Only add language header if token is provided (authenticated request)
    return apiRequest(endpoint, {
      method: 'PATCH',
      headers: getHeaders(token, language),
      body: JSON.stringify(data),
    });
  },

  // DELETE request
  delete: (endpoint, token = null) => {
    const language = token ? getUserPreferredLanguage() : null; // Only add language header if token is provided (authenticated request)
    return apiRequest(endpoint, {
      method: 'DELETE',
      headers: getHeaders(token, language),
    });
  },
}; 